import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const SplitScreenLayout = ({ 
  leftPanel, 
  rightPanel, 
  leftTitle = "Form", 
  rightTitle = "Document",
  className = '',
  leftWidth: controlledLeftWidth,
  defaultLeftWidth = 40,
  onLeftWidthChange
}) => {
  const [leftWidth, setLeftWidth] = useState(defaultLeftWidth); // 40% default
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const effectiveLeftWidth = Number.isFinite(controlledLeftWidth) ? controlledLeftWidth : leftWidth;

  const handleMouseDown = (e) => {
    setIsDragging(true);
    e?.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const container = containerRef.current;
    if (!container) return;
    
    const rect = container.getBoundingClientRect();
    const newLeftWidth = ((e.clientX - rect.left) / rect.width) * 100;
    
    // Constrain between 25% and 75%
    const constrainedWidth = Math.min(75, Math.max(25, newLeftWidth));
    if (Number.isFinite(controlledLeftWidth)) {
      onLeftWidthChange?.(constrainedWidth);
    } else {
      setLeftWidth(constrainedWidth);
      onLeftWidthChange?.(constrainedWidth);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div ref={containerRef} className={`split-container h-full flex ${className}`}>
      {/* Left Panel */}
      <div 
        className="flex flex-col border-r border-border bg-card"
        style={{ width: `${effectiveLeftWidth}%` }}
      >
        <div className="flex items-center justify-between p-3 border-b border-border bg-muted/30">
          <h3 className="text-sm font-medium text-foreground">{leftTitle}</h3>
          <div className="text-xs text-muted-foreground">
            {Math.round(effectiveLeftWidth)}%
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          {leftPanel}
        </div>
      </div>

      {/* Resize Handle */}
      <div
        className={`
          w-1 bg-border hover:bg-primary/50 cursor-col-resize transition-colors
          flex items-center justify-center relative group
          ${isDragging ? 'bg-primary' : ''}
        `}
        onMouseDown={handleMouseDown}
        title="Drag to resize panels"
      >
        <div className="absolute inset-y-0 -left-1 -right-1 flex items-center justify-center">
          <div className="w-1 h-8 bg-muted-foreground/30 rounded-full group-hover:bg-primary/70 transition-colors" />
        </div>
        
        {/* Resize indicator */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
          <Icon name="GripVertical" size={12} className="text-muted-foreground" />
        </div>
      </div>

      {/* Right Panel */}
      <div 
        className="flex flex-col bg-card"
        style={{ width: `${100 - effectiveLeftWidth}%` }}
      >
        <div className="flex items-center justify-between p-3 border-b border-border bg-muted/30">
          <h3 className="text-sm font-medium text-foreground">{rightTitle}</h3>
          <div className="text-xs text-muted-foreground">
            {Math.round(100 - effectiveLeftWidth)}%
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          {rightPanel}
        </div>
      </div>
    </div>
  );
};

export default SplitScreenLayout;
